// src/store/store.ts
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import authReducer from './slices/auth/authSlice';
import { authApi } from './api/auth/authApi';
import { projectsApi } from './api/projects/projectsApi';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { organizationsApi } from './api/organizations/organizationsApi';
import { channelsApi } from './api/channels/channelsApi';
import { bundlesApi } from './api/bundles/bundlesApi';
import { invitesApi } from './api/invites/invitesApi';
import { billingApi } from './api/billing/billingApi';
import { tokensApi } from './api/tokens/tokensApi';
import { analyticsApi } from './api/analytics/analyticsApi';
import { featureFlagsApi } from './api/featureFlags/featureFlagsApi';
import { notificationsApi } from './api/notifications/notificationsApi';
import { activityApi } from './api/activity/activityApi';
import runtimeConfigReducer from './slices/runtimeConfig/runtimeConfigSlice';

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['auth'], // only persist the auth slice
};

const rootReducer = combineReducers({
  auth: authReducer,
  runtimeConfig: runtimeConfigReducer,
  [authApi.reducerPath]: authApi.reducer,
  [projectsApi.reducerPath]: projectsApi.reducer,
  [organizationsApi.reducerPath]: organizationsApi.reducer,
  [channelsApi.reducerPath]: channelsApi.reducer,
  [bundlesApi.reducerPath]: bundlesApi.reducer,
  [invitesApi.reducerPath]: invitesApi.reducer,
  [billingApi.reducerPath]: billingApi.reducer,
  [tokensApi.reducerPath]: tokensApi.reducer,
  [analyticsApi.reducerPath]: analyticsApi.reducer,
  [featureFlagsApi.reducerPath]: featureFlagsApi.reducer,
  [notificationsApi.reducerPath]: notificationsApi.reducer,
  [activityApi.reducerPath]: activityApi.reducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: false,
    })
      .concat(authApi.middleware)
      .concat(projectsApi.middleware)
      .concat(organizationsApi.middleware)
      .concat(channelsApi.middleware)
      .concat(bundlesApi.middleware)
      .concat(invitesApi.middleware)
      .concat(billingApi.middleware)
      .concat(tokensApi.middleware)
      .concat(analyticsApi.middleware)
      .concat(featureFlagsApi.middleware)
      .concat(notificationsApi.middleware)
      .concat(activityApi.middleware),
});

setupListeners(store.dispatch);

export const persistor = persistStore(store);
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
